/**
 * Fund Search Input Component
 *
 * A specialized search input for searching mutual funds by name or ISIN.
 * Features:
 * - Debounced input (configurable delay, default 300ms)
 * - Clear button (X) to reset search
 * - Search icon
 * - Loading state indicator
 * - Proper placeholder text
 */

import * as React from "react";
import { useState, useCallback, useEffect, useRef } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";

// ============================================================================
// Types
// ============================================================================

/**
 * Props for the FundSearchInput component
 */
export interface FundSearchInputProps {
  /** Callback fired when the debounced search value changes */
  onSearch?: (value: string) => void;
  /** Callback fired on every input change (before debouncing) */
  onChange?: (value: string) => void;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Debounce delay in milliseconds (default: 300ms) */
  debounceDelay?: number;
  /** Whether the search is currently loading */
  isLoading?: boolean;
  /** Initial value for uncontrolled usage */
  defaultValue?: string;
  /** Controlled value - when provided, component becomes controlled */
  value?: string;
  /** Additional CSS classes for the wrapper */
  className?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Auto-focus the input on mount */
  autoFocus?: boolean;
  /** Minimum characters required before triggering search */
  minSearchLength?: number;
  /** aria-label for accessibility */
  "aria-label"?: string;
}

// ============================================================================
// Constants
// ============================================================================

/** Default debounce delay in milliseconds */
const DEFAULT_DEBOUNCE_DELAY = 300;

/** Default placeholder text */
const DEFAULT_PLACEHOLDER = "Search by fund name or ISIN...";

/** Default minimum search length */
const DEFAULT_MIN_SEARCH_LENGTH = 0;

// ============================================================================
// Component
// ============================================================================

/**
 * Fund Search Input
 *
 * A reusable search input component optimized for searching mutual funds.
 * Includes debouncing to prevent excessive API calls, a clear button for
 * easy reset, and visual loading state.
 *
 * Supports both controlled and uncontrolled modes:
 * - Uncontrolled: Use `defaultValue` and `onSearch`
 * - Controlled: Use `value`, `onChange`, and optionally `onSearch`
 *
 * @example
 * ```tsx
 * // Uncontrolled usage (recommended)
 * <FundSearchInput onSearch={(term) => console.log("Search:", term)} />
 *
 * // With loading state
 * <FundSearchInput
 *   onSearch={handleSearch}
 *   isLoading={isSearching}
 *   debounceDelay={500}
 * />
 *
 * // Controlled value
 * <FundSearchInput
 *   value={searchTerm}
 *   onChange={setSearchTerm}
 *   onSearch={handleSearch}
 * />
 * ```
 */
export function FundSearchInput({
  onSearch,
  onChange,
  placeholder = DEFAULT_PLACEHOLDER,
  debounceDelay = DEFAULT_DEBOUNCE_DELAY,
  isLoading = false,
  defaultValue = "",
  value: controlledValue,
  className,
  disabled = false,
  autoFocus = false,
  minSearchLength = DEFAULT_MIN_SEARCH_LENGTH,
  "aria-label": ariaLabel = "Search funds",
}: FundSearchInputProps) {
  // Determine if controlled or uncontrolled mode
  const isControlled = controlledValue !== undefined;

  // Local state for uncontrolled mode only
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);

  // Current value - either controlled or uncontrolled
  const currentValue = isControlled ? controlledValue : uncontrolledValue;

  // Debounce the search value
  const debouncedValue = useDebounce(currentValue, debounceDelay);

  // Ref for the input element
  const inputRef = useRef<HTMLInputElement>(null);

  // Track if this is the initial render to avoid triggering onSearch
  const isInitialRender = useRef(true);

  // Track previous debounced value to avoid duplicate calls
  const prevDebouncedValueRef = useRef<string>(debouncedValue);

  // Handle input change
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;

      // Update internal state in uncontrolled mode
      if (!isControlled) {
        setUncontrolledValue(newValue);
      }

      // Always notify parent of change
      onChange?.(newValue);
    },
    [isControlled, onChange]
  );

  // Handle clear button click
  const handleClear = useCallback(() => {
    // Update internal state in uncontrolled mode
    if (!isControlled) {
      setUncontrolledValue("");
    }

    // Notify parent of change and search
    onChange?.("");
    onSearch?.("");

    // Focus the input after clearing
    inputRef.current?.focus();
  }, [isControlled, onChange, onSearch]);

  // Trigger search when debounced value changes
  useEffect(() => {
    // Skip initial render
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    // Avoid calling onSearch with same value
    if (prevDebouncedValueRef.current === debouncedValue) {
      return;
    }

    prevDebouncedValueRef.current = debouncedValue;

    // Check minimum length requirement
    if (debouncedValue.length >= minSearchLength) {
      onSearch?.(debouncedValue);
    }
  }, [debouncedValue, minSearchLength, onSearch]);

  // Determine if clear button should be shown
  const showClearButton = currentValue.length > 0 && !disabled;

  // Build suffix element (clear button and/or loading indicator)
  const suffix = (
    <div className="flex items-center gap-1">
      {isLoading && (
        <Loader2
          className="h-4 w-4 animate-spin text-muted-foreground"
          aria-hidden="true"
        />
      )}
      {showClearButton && (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={handleClear}
          className="h-7 w-7 rounded-sm hover:bg-muted"
          aria-label="Clear search"
          disabled={disabled}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );

  return (
    <Input
      ref={inputRef}
      type="search"
      value={currentValue}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      autoFocus={autoFocus}
      aria-label={ariaLabel}
      aria-busy={isLoading}
      className={cn("w-full", className)}
      startAdornment={
        <Search className="h-4 w-4" aria-hidden="true" />
      }
      endAdornment={suffix}
    />
  );
}

// ============================================================================
// Skeleton Loader
// ============================================================================

/**
 * Skeleton loader for FundSearchInput
 */
export function FundSearchInputSkeleton({
  className,
}: {
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative flex h-10 w-full items-center rounded-md border border-input bg-background",
        className
      )}
    >
      <div className="pointer-events-none flex items-center pl-3 text-muted-foreground">
        <div className="h-4 w-4 rounded bg-muted animate-pulse" />
      </div>
      <div className="flex-1 h-full py-2 px-2">
        <div className="h-4 w-2/3 rounded bg-muted animate-pulse" />
      </div>
    </div>
  );
}

export default FundSearchInput;
